<template>
  <div class="card mx-2 my-4 board-card">
    <div class="board-toolbar">
      <div class="board-titles">
        <h5 class="mb-0">{{ $t('ToBillBoard.title') }}</h5>
        <p class="board-subtitle mb-0">{{ $t('ToBillBoard.subtitle') }}</p>
      </div>
      <input
        v-model="filterText"
        :placeholder="$t('ToBillBoard.filter_customers')"
        class="form-control board-filter"
        type="text"
      />
      <input
        v-model="filterRef"
        :placeholder="$t('ToBillBoard.filter_ref')"
        class="form-control board-filter"
        type="text"
      />
      <input
        v-model="filterAll"
        :placeholder="$t('ToBillBoard.filter_all')"
        class="form-control board-filter"
        type="text"
      />
      <button v-if="canEdit" class="btn btn-kne-green m-1" :disabled="selectedOrderCount < 2" @click="groupSelected">
        {{ $t('ToBillBoard.group_selected') }} ({{ selectedOrderCount }})
      </button>
      <button v-if="canEdit" class="btn btn-kne-orange m-1" :disabled="selectedDraftCount < 1" @click="sendSelected">
        {{ $t('ToBillBoard.send_selected') }} ({{ selectedDraftCount }})
      </button>
    </div>

    <div class="board-body">
      <p v-if="!visibleSections.length" class="text-center text-muted my-4">{{ $t('ToBillBoard.empty') }}</p>

      <section v-for="section in visibleSections" :key="section.payerId" class="customer-section">
        <div class="customer-header" @click="toggleSection(section.payerId)">
          <i class="fa chevron" :class="isSectionOpen(section.payerId) ? 'fa-chevron-down' : 'fa-chevron-right'" />
          <label v-if="canEdit" class="customer-check" @click.stop>
            <input
              type="checkbox"
              :checked="!!selectedCustomers[section.payerId]"
              @change="toggleCustomerSelected(section.payerId)"
            />
          </label>
          <span class="customer-name">{{ section.payerName }}</span>
          <i v-if="section.missingOdooPartner" class="fa fa-exclamation-triangle text-warning" :title="$t('ToBillBoard.no_odoo_customer')" />
          <span class="flex-spacer"></span>
          <span v-if="queuedOf(section) || doneOf(section)" class="customer-status">
            <span v-if="queuedOf(section)" class="text-primary">{{ queuedOf(section) }} {{ $t('ToBillBoard.queued') }}</span>
            <span v-if="queuedOf(section) && doneOf(section)"> · </span>
            <span v-if="doneOf(section)" class="text-success">{{ doneOf(section) }} {{ $t('ToBillBoard.done') }}</span>
          </span>
          <span class="customer-counts">
            {{ section.orders.length }} {{ $t('ToBillBoard.orders') }} ·
            <b>{{ section.invoices.length }} {{ section.invoices.length > 1 ? $t('ToBillBoard.invoices') : $t('ToBillBoard.invoice') }}</b>
          </span>
          <span class="customer-total">{{ formatMoney(sectionTotal(section)) }}</span>
          <button
            v-if="canEdit"
            class="btn btn-kne-orange btn-sm"
            :disabled="draftsOf(section) < 1"
            :title="$t('ToBillBoard.send_all_customer')"
            @click.stop="sendCustomer(section)"
          >
            {{ $t('ToBillBoard.send_all') }} ({{ draftsOf(section) }})
          </button>
        </div>

        <div v-show="isSectionOpen(section.payerId)" class="customer-body">
          <div
            v-for="group in section.groups"
            :key="group.id"
            class="group-card"
            :class="dropClass('group:' + group.id)"
            @dragover.prevent="onDragOver($event, { type: 'group', id: group.id, payerId: section.payerId, invoice: group.invoice })"
            @dragleave="onDragLeave($event, 'group:' + group.id)"
            @drop.prevent.stop="onDrop({ type: 'group', id: group.id, payerId: section.payerId, invoice: group.invoice })"
          >
            <div class="group-header">
              <i class="fa fa-file-text-o" />
              <span class="group-name">{{ groupLabel(group) }}</span>
              <span class="reason-chip" :class="'reason-' + group.reason">{{ $t('ToBillBoard.' + reasonKey(group.reason)) }}</span>
              <span class="group-counts">{{ group.orders.length }} {{ $t('ToBillBoard.orders') }} · 1 {{ $t('ToBillBoard.invoice') }}</span>
              <span class="flex-spacer"></span>
              <span v-if="dropTarget && dropTarget.key === 'group:' + group.id && !dropTarget.ok" class="drop-warning">
                <i class="fa fa-exclamation-triangle" /> {{ dropWarning }}
              </span>
              <template v-if="group.invoice.status === 'draft' && canEdit">
                <label class="inline-control">{{ $t('OrderCurrent.modal.odoo_tax') }}
                  <select class="form-control input-sm" :value="group.invoice.taxes" @change="setInvoiceTaxes(group.invoice, $event.target.value)">
                    <option v-for="code in taxCodes" :key="code" :value="code">{{ code }}</option>
                  </select>
                </label>
                <label class="inline-control">{{ $t('ToBillBoard.fuel') }} %
                  <input class="form-control input-sm fuel-input" type="number" min="0" step="0.1"
                    :value="invoiceFuelPercent(group.invoice)"
                    @change="setInvoiceFuel(group.invoice, $event.target.value)" />
                </label>
                <input class="form-control input-sm date-input" type="date"
                  :value="group.invoice.billingDate"
                  :title="$t('OrderCurrent.billingDate')"
                  @change="setInvoiceDate(group.invoice, $event.target.value)" />
              </template>
              <span class="group-total">{{ formatMoney(group.invoice.total) }}</span>
              <span class="send-cluster">
                <button v-if="group.invoice.status === 'draft' && canEdit" class="btn btn-kne-orange btn-sm"
                  :disabled="invoiceConflict(group.invoice)"
                  :title="invoiceConflict(group.invoice) ? $t('ToBillBoard.price_conflict') : ''"
                  @click="sendInvoice(group.invoice)">
                  {{ $t('ToBillBoard.send') }}
                </button>
                <span v-else-if="group.invoice.status === 'queued'" class="status-chip chip-queued">{{ $t('OrderCurrent.modal.odoo_sync_requested') }}</span>
                <span v-else-if="group.invoice.status === 'done'" class="status-chip chip-done"><i class="fa fa-check" /> {{ $t('ToBillBoard.done') }}</span>
                <button v-if="canResendInvoice(group.invoice)" class="btn btn-kne-orange btn-sm"
                  :disabled="invoiceConflict(group.invoice)"
                  :title="invoiceConflict(group.invoice) ? $t('ToBillBoard.price_conflict') : ''"
                  @click="resendInvoice(group.invoice)">
                  {{ $t('ToBillBoard.resend') }}
                </button>
                <button v-if="group.invoice.status === 'done' && canEdit"
                  class="btn btn-kne-green btn-sm" @click="markInvoiceDone(group.invoice)">
                  {{ $t('ToBillBoard.mark_done') }}
                </button>
              </span>
              <button
                v-if="group.invoice.status === 'draft' && canEdit"
                class="btn-icon"
                :title="$t('ToBillBoard.break_group')"
                @click="dissolveGroup(group)"
              ><i class="fa fa-times" /></button>
            </div>

            <div class="group-orders">
              <div v-for="order in group.orders" :key="order['.key']" class="order-row-wrap">
                <div
                  class="order-row"
                  :class="{ 'price-conflict': hasPriceConflict(order) }"
                  :draggable="canDrag(group.invoice)"
                  @dragstart="onDragStart($event, order)"
                  @dragend="onDragEnd"
                >
                  <span class="grip" :class="{ 'grip-off': !canDrag(group.invoice) }"><i class="fa fa-ellipsis-v" /></span>
                  <label v-if="canDrag(group.invoice)" class="row-check" @click.stop>
                    <input
                      type="checkbox"
                      :checked="!!selectedOrders[order['.key']]"
                      @change="toggleOrderSelected(order['.key'])"
                    />
                  </label>
                  <router-link class="order-key" :to="{ name: 'OrderInfo', query: { orderId: order['.key'] } }">{{ order['.key'] }}</router-link>
                  <span class="order-ref" :title="order.numRef">{{ order.numRef || '—' }}</span>
                  <span class="order-date" :title="creationDateTime(order)">{{ creationDate(order) }}</span>
                  <span class="order-vin" :title="order.vin">{{ truncate(order.vin, 14) }}</span>
                  <span class="order-model" :title="order.model">{{ truncate(order.model, 18) }}</span>
                  <span class="order-route">
                    <span class="route-loc">{{ $realName(order, 'transmitter') || '—' }}
                      <hover-tool-tip :text="fullAddress(order, 'transmitter')" :displayFixed="true" bottom right />
                    </span>
                    <span class="route-arrow">→</span>
                    <span class="route-loc">{{ $realName(order, 'receiver') || '—' }}
                      <b v-if="order.receiverProvince">({{ order.receiverProvince }})</b>
                      <hover-tool-tip :text="fullAddress(order, 'receiver')" :displayFixed="true" bottom right />
                    </span>
                  </span>
                  <i v-if="hasPriceConflict(order)" class="fa fa-exclamation-circle text-danger" :title="$t('ToBillBoard.price_conflict')" />
                  <template v-if="group.invoice.status === 'done' && canEdit">
                    <label class="inline-control done-billing-field">{{ $t('ToBillBoard.amount') }}
                      <input class="form-control input-sm price-input" type="number" min="0" step="0.01"
                        :value="order.price" @input="setDoneOrderPrice(order, $event.target.value)" />
                    </label>
                    <label class="inline-control done-billing-field">{{ $t('ToBillBoard.fuel') }} %
                      <input class="form-control input-sm fuel-input" type="number" min="0" step="0.1"
                        :value="doneOrderFuelValue(order)" @input="setDoneOrderFuel(order, $event.target.value)" />
                    </label>
                  </template>
                  <span v-else class="order-price">{{ formatMoney(order.price) }}</span>
                  <button class="btn-icon" @click="toggleExpand(order['.key'])">
                    <i class="fa" :class="isExpanded(order['.key']) ? 'fa-chevron-up' : 'fa-chevron-down'" />
                  </button>
                  <button
                    v-if="canDrag(group.invoice)"
                    class="btn-icon remove-order"
                    :title="$t('ToBillBoard.remove_from_group')"
                    @click="removeFromGroup(group, order)"
                  ><i class="fa fa-times" /></button>
                </div>
                <div v-if="isExpanded(order['.key'])" class="order-details">
                  <div class="details-info">
                    <span><i class="fa fa-user" /> {{ order.driverName || '-' }}</span>
                    <span>{{ $t('ToBillBoard.expected') }} <b>{{ formatMoney(order.regularPrice) }}</b></span>
                    <span class="mono">{{ order.vin }}</span>
                    <label v-if="group.invoice.status === 'draft' && canEdit" class="inline-control">{{ $t('ToBillBoard.price') }}
                      <input class="form-control input-sm price-input" type="number" min="0" step="0.01"
                        :value="order.price" @change="setOrderPrice(order, $event.target.value)" />
                    </label>
                  </div>

                  <div class="details-block">
                    <div class="details-title">{{ $t('ToBillBoard.history') }}</div>
                    <table v-if="historyEntries(order).length" class="history-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>{{ $t('OrderCurrent.driver') }}</th>
                          <th>{{ $t('OrderCurrent.pickup') }} → {{ $t('OrderCurrent.delivery') }}</th>
                          <th>{{ $t('HistoryPage.pickTime') }}</th>
                          <th>{{ $t('HistoryPage.dropTime') }}</th>
                          <th>{{ $t('Status.status') }}</th>
                          <th>{{ $t('HistoryPage.bill_number') }}</th>
                          <th class="num">{{ $t('OrderCurrent.price') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(entry, index) in historyEntries(order)" :key="entry.key">
                          <td>{{ index + 1 }}</td>
                          <td>{{ entry.chauffeurName || '-' }}</td>
                          <td class="route-cell" :title="`${$realName(entry, 'transmitter') || ''} → ${$realName(entry, 'receiver') || ''}`">
                            {{ truncate($realName(entry, 'transmitter'), 16) }} → {{ truncate($realName(entry, 'receiver'), 16) }}
                          </td>
                          <td :title="fullDateTime(entry.pickDate)">{{ shortDate(entry.pickDate) }}</td>
                          <td :title="fullDateTime(entry.dropDate)">{{ shortDate(entry.dropDate) }}</td>
                          <td><span class="status-chip" :class="entryStatusClass(entry)">{{ $t('ToBillBoard.' + entryStatusKey(entry)) }}</span></td>
                          <td>{{ entry.numBill || '-' }}</td>
                          <td class="num">
                            <input v-if="group.invoice.status === 'draft' && canEdit"
                              class="form-control input-sm history-price-input" type="number" min="0" step="0.01"
                              :value="entryPrice(entry)" @change="setEntryPrice(order, entry, $event.target.value)" />
                            <template v-else>{{ formatMoney(entryPrice(entry)) }}</template>
                          </td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr :class="{ 'history-over': hasPriceConflict(order) }">
                          <td colspan="7" class="num">{{ $t('ToBillBoard.parts_total') }}</td>
                          <td class="num"><b>{{ formatMoney(historyTotal(order)) }}</b> / {{ formatMoney(order.price) }}</td>
                        </tr>
                      </tfoot>
                    </table>
                    <p v-else class="details-empty">{{ $t('ToBillBoard.no_history') }}</p>
                    <p v-if="hasPriceConflict(order)" class="conflict-msg">
                      <i class="fa fa-exclamation-circle" /> {{ $t('ToBillBoard.price_conflict') }}
                    </p>
                  </div>

                  <div class="details-block">
                    <div class="details-title">{{ $t('ToBillBoard.notes') }}</div>
                    <ul v-if="notesList(order).length" class="notes-list">
                      <li v-for="(note, index) in notesList(order)" :key="index">
                        <span class="note-meta">{{ fullDateTime(note.date) }} — {{ note.name || '-' }}</span>
                        <span class="note-text">{{ note.text }}</span>
                      </li>
                    </ul>
                    <p v-else class="details-empty">{{ $t('ToBillBoard.no_notes') }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="group-breakdown">
              <span>{{ $t('OrderCurrent.modal.odoo_orders') }} <b>{{ formatMoney(group.invoice.ordersTotal) }}</b></span>
              <span>+ {{ $t('ToBillBoard.fuel') }} <b>{{ formatMoney(group.invoice.fuelTotal) }}</b></span>
              <span>+ {{ $t('OrderCurrent.modal.odoo_tax') }} <b>{{ formatMoney(group.invoice.taxAmount) }}</b></span>
              <span>= <b class="grand">{{ formatMoney(group.invoice.total) }}</b></span>
            </div>
          </div>

          <div
            class="ungrouped-zone"
            :class="dropClass('ungrouped:' + section.payerId)"
            @dragover.prevent="onDragOver($event, { type: 'ungrouped', id: section.payerId, payerId: section.payerId })"
            @dragleave="onDragLeave($event, 'ungrouped:' + section.payerId)"
            @drop.prevent.stop="onDrop({ type: 'ungrouped', id: section.payerId, payerId: section.payerId })"
          >
            <div class="zone-label">
              {{ $t('ToBillBoard.one_invoice_each') }}
              <span v-if="dropTarget && dropTarget.key === 'ungrouped:' + section.payerId && !dropTarget.ok" class="drop-warning">
                <i class="fa fa-exclamation-triangle" /> {{ dropWarning }}
              </span>
            </div>
            <p v-if="!section.solos.length" class="zone-empty">{{ $t('ToBillBoard.drop_to_solo') }}</p>
            <div v-for="solo in section.solos" :key="solo.invoice.key" class="order-row-wrap solo-row-wrap">
              <div
                class="order-row solo-row"
                :class="{ 'price-conflict': hasPriceConflict(solo.order) }"
                :draggable="canDrag(solo.invoice)"
                @dragstart="onDragStart($event, solo.order)"
                @dragend="onDragEnd"
              >
                <span class="grip" :class="{ 'grip-off': !canDrag(solo.invoice) }"><i class="fa fa-ellipsis-v" /></span>
                <label v-if="canDrag(solo.invoice)" class="row-check" @click.stop>
                  <input
                    type="checkbox"
                    :checked="!!selectedOrders[solo.order['.key']]"
                    @change="toggleOrderSelected(solo.order['.key'])"
                  />
                </label>
                <router-link class="order-key" :to="{ name: 'OrderInfo', query: { orderId: solo.order['.key'] } }">{{ solo.order['.key'] }}</router-link>
                <span class="order-ref" :title="solo.order.numRef">{{ solo.order.numRef || '—' }}</span>
                <span class="order-date" :title="creationDateTime(solo.order)">{{ creationDate(solo.order) }}</span>
                <span class="order-model" :title="solo.order.model">{{ truncate(solo.order.model, 18) }}</span>
                <span class="order-route">
                  <span class="route-loc">{{ $realName(solo.order, 'transmitter') || '—' }}
                    <hover-tool-tip :text="fullAddress(solo.order, 'transmitter')" :displayFixed="true" bottom right />
                  </span>
                  <span class="route-arrow">→</span>
                  <span class="route-loc">{{ $realName(solo.order, 'receiver') || '—' }}
                    <b v-if="solo.order.receiverProvince">({{ solo.order.receiverProvince }})</b>
                    <hover-tool-tip :text="fullAddress(solo.order, 'receiver')" :displayFixed="true" bottom right />
                  </span>
                </span>
                <span class="flex-spacer"></span>
                <template v-if="solo.invoice.status === 'draft' && canEdit">
                  <label class="inline-control">{{ $t('OrderCurrent.modal.odoo_tax') }}
                    <select class="form-control input-sm" :value="solo.invoice.taxes" @change="setInvoiceTaxes(solo.invoice, $event.target.value)">
                      <option v-for="code in taxCodes" :key="code" :value="code">{{ code }}</option>
                    </select>
                  </label>
                  <label class="inline-control">{{ $t('ToBillBoard.fuel') }} %
                    <input class="form-control input-sm fuel-input" type="number" min="0" step="0.1"
                      :value="invoiceFuelPercent(solo.invoice)"
                      @change="setInvoiceFuel(solo.invoice, $event.target.value)" />
                  </label>
                  <input class="form-control input-sm date-input" type="date"
                    :value="solo.invoice.billingDate"
                    :title="$t('OrderCurrent.billingDate')"
                    @change="setInvoiceDate(solo.invoice, $event.target.value)" />
                </template>
                <i v-if="hasPriceConflict(solo.order)" class="fa fa-exclamation-circle text-danger" :title="$t('ToBillBoard.price_conflict')" />
                <template v-if="solo.invoice.status === 'done' && canEdit">
                  <label class="inline-control done-billing-field">{{ $t('ToBillBoard.amount') }}
                    <input class="form-control input-sm price-input" type="number" min="0" step="0.01"
                      :value="solo.order.price" @input="setDoneOrderPrice(solo.order, $event.target.value)" />
                  </label>
                  <label class="inline-control done-billing-field">{{ $t('ToBillBoard.fuel') }} %
                    <input class="form-control input-sm fuel-input" type="number" min="0" step="0.1"
                      :value="doneOrderFuelValue(solo.order)" @input="setDoneOrderFuel(solo.order, $event.target.value)" />
                  </label>
                </template>
                <span class="order-price">{{ formatMoney(solo.invoice.total) }}</span>
                <span class="send-cluster">
                  <button v-if="solo.invoice.status === 'draft' && canEdit" class="btn btn-kne-orange btn-sm"
                    :disabled="invoiceConflict(solo.invoice)"
                    :title="invoiceConflict(solo.invoice) ? $t('ToBillBoard.price_conflict') : ''"
                    @click="sendInvoice(solo.invoice)">
                    {{ $t('ToBillBoard.send') }}
                  </button>
                  <span v-else-if="solo.invoice.status === 'queued'" class="status-chip chip-queued">{{ $t('OrderCurrent.modal.odoo_sync_requested') }}</span>
                  <span v-else-if="solo.invoice.status === 'done'" class="status-chip chip-done"><i class="fa fa-check" /> {{ $t('ToBillBoard.done') }}</span>
                  <button v-if="canResendInvoice(solo.invoice)" class="btn btn-kne-orange btn-sm"
                    :disabled="invoiceConflict(solo.invoice)"
                    :title="invoiceConflict(solo.invoice) ? $t('ToBillBoard.price_conflict') : ''"
                    @click="resendInvoice(solo.invoice)">
                    {{ $t('ToBillBoard.resend') }}
                  </button>
                  <button v-if="solo.invoice.status === 'done' && canEdit"
                    class="btn btn-kne-green btn-sm" @click="markInvoiceDone(solo.invoice)">
                    {{ $t('ToBillBoard.mark_done') }}
                  </button>
                </span>
                <button class="btn-icon" @click="toggleExpand(solo.order['.key'])">
                  <i class="fa" :class="isExpanded(solo.order['.key']) ? 'fa-chevron-up' : 'fa-chevron-down'" />
                </button>
              </div>
              <div v-if="isExpanded(solo.order['.key'])" class="order-details">
                <div class="details-info">
                  <span><i class="fa fa-user" /> {{ solo.order.driverName || '-' }}</span>
                  <span>{{ $t('ToBillBoard.expected') }} <b>{{ formatMoney(solo.order.regularPrice) }}</b></span>
                  <span class="mono">{{ solo.order.vin }}</span>
                  <label v-if="solo.invoice.status === 'draft' && canEdit" class="inline-control">{{ $t('ToBillBoard.price') }}
                    <input class="form-control input-sm price-input" type="number" min="0" step="0.01"
                      :value="solo.order.price" @change="setOrderPrice(solo.order, $event.target.value)" />
                  </label>
                </div>

                <div class="details-block">
                  <div class="details-title">{{ $t('ToBillBoard.history') }}</div>
                  <table v-if="historyEntries(solo.order).length" class="history-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{{ $t('OrderCurrent.driver') }}</th>
                        <th>{{ $t('OrderCurrent.pickup') }} → {{ $t('OrderCurrent.delivery') }}</th>
                        <th>{{ $t('HistoryPage.pickTime') }}</th>
                        <th>{{ $t('HistoryPage.dropTime') }}</th>
                        <th>{{ $t('Status.status') }}</th>
                        <th>{{ $t('HistoryPage.bill_number') }}</th>
                        <th class="num">{{ $t('OrderCurrent.price') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(entry, index) in historyEntries(solo.order)" :key="entry.key">
                        <td>{{ index + 1 }}</td>
                        <td>{{ entry.chauffeurName || '-' }}</td>
                        <td class="route-cell" :title="`${$realName(entry, 'transmitter') || ''} → ${$realName(entry, 'receiver') || ''}`">
                          {{ truncate($realName(entry, 'transmitter'), 16) }} → {{ truncate($realName(entry, 'receiver'), 16) }}
                        </td>
                        <td :title="fullDateTime(entry.pickDate)">{{ shortDate(entry.pickDate) }}</td>
                        <td :title="fullDateTime(entry.dropDate)">{{ shortDate(entry.dropDate) }}</td>
                        <td><span class="status-chip" :class="entryStatusClass(entry)">{{ $t('ToBillBoard.' + entryStatusKey(entry)) }}</span></td>
                        <td>{{ entry.numBill || '-' }}</td>
                        <td class="num">
                          <input v-if="solo.invoice.status === 'draft' && canEdit"
                            class="form-control input-sm history-price-input" type="number" min="0" step="0.01"
                            :value="entryPrice(entry)" @change="setEntryPrice(solo.order, entry, $event.target.value)" />
                          <template v-else>{{ formatMoney(entryPrice(entry)) }}</template>
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr :class="{ 'history-over': hasPriceConflict(solo.order) }">
                        <td colspan="7" class="num">{{ $t('ToBillBoard.parts_total') }}</td>
                        <td class="num"><b>{{ formatMoney(historyTotal(solo.order)) }}</b> / {{ formatMoney(solo.order.price) }}</td>
                      </tr>
                    </tfoot>
                  </table>
                  <p v-else class="details-empty">{{ $t('ToBillBoard.no_history') }}</p>
                  <p v-if="hasPriceConflict(solo.order)" class="conflict-msg">
                    <i class="fa fa-exclamation-circle" /> {{ $t('ToBillBoard.price_conflict') }}
                  </p>
                </div>

                <div class="details-block">
                  <div class="details-title">{{ $t('ToBillBoard.notes') }}</div>
                  <ul v-if="notesList(solo.order).length" class="notes-list">
                    <li v-for="(note, index) in notesList(solo.order)" :key="index">
                      <span class="note-meta">{{ fullDateTime(note.date) }} — {{ note.name || '-' }}</span>
                      <span class="note-text">{{ note.text }}</span>
                    </li>
                  </ul>
                  <p v-else class="details-empty">{{ $t('ToBillBoard.no_notes') }}</p>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="canEdit"
            class="new-group-zone"
            :class="dropClass('new:' + section.payerId)"
            @dragover.prevent="onDragOver($event, { type: 'new', id: section.payerId, payerId: section.payerId })"
            @dragleave="onDragLeave($event, 'new:' + section.payerId)"
            @drop.prevent.stop="onDrop({ type: 'new', id: section.payerId, payerId: section.payerId })"
          >
            <i class="fa fa-plus" />
            <span v-if="dropTarget && dropTarget.key === 'new:' + section.payerId && !dropTarget.ok">{{ dropWarning }}</span>
            <span v-else>{{ $t('ToBillBoard.drop_new_group') }}</span>
          </div>
        </div>
      </section>
    </div>

    <div class="board-footer">
      <span><b>{{ allInvoices.length }}</b> {{ $t('ToBillBoard.invoices') }}</span>
      <span class="footer-muted">
        <b>{{ draftCount }}</b> {{ $t('ToBillBoard.drafts') }}
        <template v-if="queuedCount"> · <b class="text-info">{{ queuedCount }}</b> {{ $t('ToBillBoard.queued') }}</template>
        <template v-if="doneCount"> · <b class="text-success">{{ doneCount }}</b> {{ $t('ToBillBoard.done') }}</template>
      </span>
      <span><b>{{ orders.length }}</b> {{ $t('ToBillBoard.orders') }}</span>
      <span class="footer-divider"></span>
      <span v-if="selectionActive" class="selection-chip">{{ $t('ToBillBoard.selection') }} ({{ selectedSectionCount }})</span>
      <span class="footer-muted">{{ $t('OrderCurrent.modal.odoo_subtotal') }} <b>{{ formatMoney(footerTotals.subtotal) }}</b></span>
      <span class="footer-muted">{{ $t('OrderCurrent.modal.odoo_tax') }} <b>{{ formatMoney(footerTotals.taxAmount) }}</b></span>
      <span class="footer-total">{{ $t('OrderCurrent.modal.odoo_total') }} {{ formatMoney(footerTotals.total) }}</span>
      <span class="flex-spacer"></span>
      <button v-if="canEdit && verifiedQueuedCount" class="btn btn-kne-green m-1" @click="markAllSyncedDone">
        {{ $t('ToBillBoard.mark_synced_done') }} ({{ verifiedQueuedCount }})
      </button>
      <button v-if="canEdit && selectedOrderCount" class="btn btn-kne-green m-1" :disabled="selectedOrderCount < 2" @click="groupSelected">
        {{ $t('ToBillBoard.group_selected') }} ({{ selectedOrderCount }})
      </button>
      <button v-if="canEdit" class="btn btn-kne-orange m-1" :disabled="selectedDraftCount < 1" @click="sendSelected">
        {{ $t('ToBillBoard.send_selected') }} ({{ selectedDraftCount }})
      </button>
    </div>

    <div v-if="feedback" class="board-toast" :class="'toast-' + feedbackKind">{{ feedback }}</div>
  </div>
</template>

<script>
import { db } from '../../../../firebase'
import { publishBillBatchSyncRequest } from '../../../../odoo-sync-requests/bill'
import { publishOrderSyncRequest } from '../../../../odoo-sync-requests/order'
import * as odooBilling from '../../../../utils/odooBilling'
import hoverToolTip from '../../basic-components/hoverToolTip.vue'

const MONEY_FORMAT = new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' })
const DATE_FORMAT = new Intl.DateTimeFormat()
const TIME_FORMAT = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' })

export default {
  name: 'ToBillGroupBoard',

  components: {
    hoverToolTip
  },

  props: {
    orders: {
      type: Array,
      required: true
    }
  },

  data () {
    return {
      filterText: '',
      filterRef: '',
      filterAll: '',
      openSections: {},
      expandedOrders: {},
      selectedCustomers: {},
      selectedOrders: {},
      groups: [],
      ungroupedKeys: [],
      storageLoaded: false,
      drag: null,
      dropTarget: null,
      dropWarning: '',
      feedback: '',
      feedbackKind: 'info',
      feedbackTimer: null,
      pageLoadedAt: Date.now(),
      sending: false
    }
  },

  watch: {
    orders: {
      immediate: true,
      handler () {
        this.autoGroup()
      }
    }
  },

  computed: {
    companyUID () {
      return this.$store.state.authorization.company
    },
    uid () {
      return this.$store.state.user.uid
    },
    userInfo () {
      return this.$store.state.userInfo
    },
    defaultFuelCharge () {
      return this.$store.state.userInfo.fuelCharge || 0
    },
    hasFullAccess () {
      return this.$store.state.userInfo.userType === 'full'
    },
    isViewer () {
      return this.$store.state.authorization.role === 'Viewer'
    },
    canEdit () {
      return this.hasFullAccess && !this.isViewer && !this.sending
    },
    taxCodes () {
      return Object.keys(odooBilling.TAX_RATES)
    },
    ordersByKey () {
      const map = {}
      this.orders.forEach(order => { map[order['.key']] = order })
      return map
    },
    conflictByKey () {
      const map = {}
      this.orders.forEach(order => { map[order['.key']] = this.computePriceConflict(order) })
      return map
    },
    validSelectedKeys () {
      return Object.keys(this.selectedOrders).filter(key => {
        if (!this.selectedOrders[key]) return false
        const order = this.ordersByKey[key]
        return order && this.invoiceStatus([order]) === 'draft'
      })
    },
    selectedOrderCount () {
      return this.validSelectedKeys.length
    },
    sections () {
      const map = {}
      const sectionFor = (order) => {
        const payerId = order.payer || 'no-payer'
        if (!map[payerId]) {
          map[payerId] = { payerId, payerName: this.$realName(order, 'payer') || this.$t('ToBillBoard.no_payer'), orders: [], rawGroups: [] }
        }
        return map[payerId]
      }
      this.orders.forEach(order => sectionFor(order).orders.push(order))

      const groupedKeys = new Set()
      this.groups.forEach(stored => {
        const members = stored.orderKeys.map(key => this.ordersByKey[key]).filter(Boolean)
        if (!members.length) return
        const payerId = members[0].payer || 'no-payer'
        const samePayer = members.filter(order => (order.payer || 'no-payer') === payerId)
        samePayer.forEach(order => groupedKeys.add(order['.key']))
        sectionFor(samePayer[0]).rawGroups.push({ id: stored.id, orders: samePayer })
      })

      const sections = Object.values(map)
      sections.forEach(section => {
        section.groups = section.rawGroups.map(raw => ({
          id: raw.id,
          orders: raw.orders,
          reason: this.groupReason(raw.orders),
          invoice: this.makeInvoice(raw.orders, raw.id)
        }))
        section.solos = section.orders
          .filter(order => !groupedKeys.has(order['.key']))
          .map(order => ({ order, invoice: this.makeInvoice([order], '') }))
        section.invoices = [...section.groups.map(g => g.invoice), ...section.solos.map(s => s.invoice)]
        section.missingOdooPartner = section.invoices.some(invoice => !invoice.payerOdooPartnerId)
      })

      return sections.sort((a, b) =>
        b.orders.length - a.orders.length || (a.payerName || '').localeCompare(b.payerName || ''))
    },
    storageKey () {
      return `toBillGroups-${this.companyUID}`
    },
    visibleSections () {
      const nameQ = this.filterText.trim().toLowerCase()
      const refQ = this.filterRef.trim().toLowerCase()
      const allQ = this.filterAll.trim().toLowerCase()

      let sections = this.sections
      if (nameQ) sections = sections.filter(section => (section.payerName || '').toLowerCase().includes(nameQ))
      if (!refQ && !allQ) return sections

      const matches = (order) => {
        if (refQ && !(order.numRef || '').toLowerCase().includes(refQ)) return false
        if (allQ && !this.deepMatch(order, allQ)) return false
        return true
      }

      return sections
        .map(section => ({
          ...section,
          groups: section.groups.filter(group => group.orders.some(matches)),
          solos: section.solos.filter(solo => matches(solo.order))
        }))
        .filter(section => section.groups.length || section.solos.length)
    },
    allInvoices () {
      return this.sections.reduce((list, section) => list.concat(section.invoices), [])
    },
    draftCount () {
      return this.allInvoices.filter(invoice => invoice.status === 'draft').length
    },
    queuedCount () {
      return this.allInvoices.filter(invoice => invoice.status === 'queued').length
    },
    doneCount () {
      return this.allInvoices.filter(invoice => invoice.status === 'done').length
    },
    verifiedQueuedCount () {
      return this.allInvoices.filter(invoice => invoice.status === 'done').length
    },
    selectedInvoices () {
      return this.sections
        .filter(section => this.selectedCustomers[section.payerId])
        .reduce((list, section) => list.concat(section.invoices), [])
    },
    selectedDraftCount () {
      return this.selectedInvoices.filter(invoice => invoice.status === 'draft').length
    },
    selectionActive () {
      return this.selectedSectionCount > 0
    },
    selectedSectionCount () {
      return this.sections.filter(section => this.selectedCustomers[section.payerId]).length
    },
    footerTotals () {
      if (!this.selectionActive) return this.boardTotals
      return this.selectedInvoices.reduce((totals, invoice) => {
        totals.subtotal += invoice.subtotal
        totals.taxAmount += invoice.taxAmount
        totals.total += invoice.total
        return totals
      }, { subtotal: 0, taxAmount: 0, total: 0 })
    },
    boardTotals () {
      return this.allInvoices.reduce((totals, invoice) => {
        totals.subtotal += invoice.subtotal
        totals.taxAmount += invoice.taxAmount
        totals.total += invoice.total
        return totals
      }, { subtotal: 0, taxAmount: 0, total: 0 })
    }
  },

  methods: {
    orderRef (orderKey) {
      return db.ref(`database/transporter/${this.companyUID}/deliveries/${orderKey}`)
    },
    updateOrders (orders, fields) {
      return Promise.all(orders.map(order => this.orderRef(order['.key']).update(fields)))
    },
    formatMoney (value) {
      return MONEY_FORMAT.format(Number(value) || 0)
    },
    truncate (text, len) {
      if (!text) return '—'
      return text.length > len ? text.slice(0, len) + '…' : text
    },
    creationDate (order) {
      return this.shortDate(order.creationDate)
    },
    creationDateTime (order) {
      return this.fullDateTime(order.creationDate)
    },
    shortDate (timestamp) {
      if (!timestamp) return '—'
      return DATE_FORMAT.format(new Date(timestamp))
    },
    fullDateTime (timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      return DATE_FORMAT.format(date) + ' ' + TIME_FORMAT.format(date)
    },
    historyEntries (order) {
      const history = order.historySync
      if (!history || typeof history !== 'object') return []
      return Object.keys(history)
        .map(key => ({ key, ...history[key] }))
        .filter(entry => entry && typeof entry === 'object')
    },
    entryPrice (entry) {
      if (entry.price !== undefined && entry.price !== null && entry.price !== '') return Number(entry.price) || 0
      if (entry.prix !== undefined && entry.prix !== null && entry.prix !== '') return Number(entry.prix) || 0
      return 0
    },
    historyTotal (order) {
      return this.historyEntries(order).reduce((total, entry) => total + this.entryPrice(entry), 0)
    },
    computePriceConflict (order) {
      const entries = this.historyEntries(order)
      if (!entries.length) return false
      return this.historyTotal(order) > (Number(order.price) || 0) + 0.005
    },
    hasPriceConflict (order) {
      const cached = this.conflictByKey[order['.key']]
      return cached === undefined ? this.computePriceConflict(order) : cached
    },
    invoiceConflict (invoice) {
      return invoice.orders.some(order => this.hasPriceConflict(order))
    },
    entryStatusKey (entry) {
      if (entry.dropDate) return 'status_delivered'
      if (entry.pickDate) return 'status_transit'
      return 'status_dispatched'
    },
    entryStatusClass (entry) {
      if (entry.dropDate) return 'chip-done'
      if (entry.pickDate) return 'chip-queued'
      return 'chip-neutral'
    },
    notesList (order) {
      const notes = order.notes
      if (!notes || typeof notes !== 'object') return []
      return Object.keys(notes)
        .map(key => notes[key])
        .filter(note => note && typeof note === 'object' && (note.text || note.name))
        .sort((a, b) => (a.date || 0) - (b.date || 0))
    },
    fullAddress (order, side) {
      const parts = [
        this.$realName(order, side),
        order[side + 'Address'],
        [order[side + 'City'], order[side + 'Province']].filter(Boolean).join(', '),
        [order[side + 'Postcode'], order[side + 'Country']].filter(Boolean).join(' ')
      ].filter(Boolean)
      return parts.join('<br>') || '-'
    },
    deepMatch (value, query) {
      if (value === null || value === undefined) return false
      if (typeof value === 'object') {
        return Object.keys(value).some(key => this.deepMatch(value[key], query))
      }
      return String(value).toLowerCase().includes(query)
    },
    notify (message, kind = 'info') {
      this.feedback = message
      this.feedbackKind = kind
      clearTimeout(this.feedbackTimer)
      this.feedbackTimer = setTimeout(() => { this.feedback = '' }, 3500)
    },

    toggleSection (payerId) {
      this.$set(this.openSections, payerId, !this.openSections[payerId])
    },
    isSectionOpen (payerId) {
      return !!this.openSections[payerId]
    },
    toggleExpand (orderKey) {
      this.$set(this.expandedOrders, orderKey, !this.expandedOrders[orderKey])
    },
    isExpanded (orderKey) {
      return !!this.expandedOrders[orderKey]
    },

    invoiceStatus (orders) {
      if (orders.every(order => odooBilling.getOdooReference(order))) return 'done'
      if (orders.some(order => order.odooSyncStatus === 'sync_requested')) return 'queued'
      return 'draft'
    },
    isResendableQueuedOrders (orders) {
      if (!orders.length || this.invoiceStatus(orders) !== 'queued') return false
      if (orders.some(order => odooBilling.getOdooReference(order))) return false

      const queuedOrders = orders.filter(order => order.odooSyncStatus === 'sync_requested')
      return queuedOrders.length > 0 && queuedOrders.every(order => {
        const requestedAt = Number(order.odooSyncRequestedAt)
        return !requestedAt || requestedAt < this.pageLoadedAt
      })
    },
    isResendableQueuedInvoice (invoice) {
      return !!(invoice && Array.isArray(invoice.orders) && this.isResendableQueuedOrders(invoice.orders))
    },
    canResendInvoice (invoice) {
      return this.canEdit && this.isResendableQueuedInvoice(invoice)
    },
    invoiceObject (orders, groupId) {
      const first = orders[0]
      const partnerSource = orders.find(order => order.payerOdooPartnerId) || first
      return {
        orders,
        invoiceKey: groupId || odooBilling.getOrderNumber(first),
        odooInvoiceGroupId: groupId || '',
        taxes: first.taxes || odooBilling.getDefaultTaxCode(first),
        taxesComment: first.taxesComment || '',
        billingDate: this.normalizeBillingDate(first.billingDate),
        payer: first.payer || '',
        payerName: first.payerName || '',
        payerOdooPartnerId: partnerSource.payerOdooPartnerId || '',
        payerOdooPartnerName: partnerSource.payerOdooPartnerName || '',
        zeroTax: first.zeroTax,
        payerZeroTax: first.payerZeroTax,
        transmitterProvince: first.transmitterProvince,
        transmitterCountry: first.transmitterCountry,
        receiverCountry: first.receiverCountry,
        receiverProvince: first.receiverProvince
      }
    },
    makeInvoice (orders, groupId) {
      const invoice = this.invoiceObject(orders, groupId)
      invoice.key = groupId || 'solo-' + orders[0]['.key']
      invoice.status = this.invoiceStatus(orders)
      invoice.ordersTotal = orders.reduce((total, order) =>
        total + (Number(order.price) || 0) + odooBilling.getOrderFeesTotal(order), 0)
      invoice.fuelTotal = orders.reduce((total, order) =>
        total + odooBilling.getOrderFuelAmount(order, this.defaultFuelCharge), 0)
      invoice.subtotal = odooBilling.getInvoiceSubtotal(invoice, this.defaultFuelCharge)
      invoice.taxAmount = odooBilling.getInvoiceTaxAmount(invoice, this.defaultFuelCharge)
      invoice.total = odooBilling.getInvoiceTotal(invoice, this.defaultFuelCharge)
      return invoice
    },
    invoiceVerified (invoice) {
      return invoice.orders.every(order => odooBilling.getOdooReference(order))
    },
    invoiceFuelPercent (invoice) {
      return odooBilling.getFuelChargePercent(invoice.orders[0], this.defaultFuelCharge)
    },
    normalizeBillingDate (value) {
      const text = String(value || '').trim()
      if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text
      if (!text) return odooBilling.formatDateYMD(Date.now())
      return odooBilling.formatDateYMD(text)
    },

    groupReason (orders) {
      if (orders.length < 2) return 'manual'
      const refs = orders.map(order => order.numRef).filter(Boolean)
      if (refs.length === orders.length && new Set(refs).size === 1) return 'reference'
      if (new Set(orders.map(order => odooBilling.getDefaultTaxCode(order))).size === 1) return 'tax'
      return 'manual'
    },
    reasonKey (reason) {
      return reason === 'reference' ? 'same_reference' : reason === 'tax' ? 'same_tax' : 'manual_group'
    },
    groupLabel (group) {
      if (group.reason === 'reference') return group.orders[0].numRef
      if (group.reason === 'tax') return odooBilling.getDefaultTaxCode(group.orders[0])
      return this.$t('OrderCurrent.modal.odoo_group')
    },

    setInvoiceTaxes (invoice, value) {
      this.updateOrders(invoice.orders, { taxes: value })
    },
    setInvoiceDate (invoice, value) {
      const billingDate = this.normalizeBillingDate(value)
      this.$set(invoice, 'billingDate', billingDate)
      invoice.orders.forEach(order => {
        this.$set(order, 'billingDate', billingDate)
      })
      return this.updateOrders(invoice.orders, { billingDate })
    },
    setInvoiceFuel (invoice, value) {
      const fields = value === ''
        ? { fuelCharge: '' }
        : { fuelCharge: value, includedFuelCharge: false, applyFuelCharge: true }
      this.updateOrders(invoice.orders, fields)
    },
    setOrderPrice (order, value) {
      this.orderRef(order['.key']).update({ price: Number(value) || 0 })
    },
    doneOrderFuelValue (order) {
      return odooBilling.getFuelChargePercent(order, this.defaultFuelCharge)
    },
    setDoneOrderPrice (order, value) {
      this.$set(order, 'price', value)
    },
    setDoneOrderFuel (order, value) {
      this.$set(order, 'fuelCharge', value)
      if (value !== '') {
        this.$set(order, 'includedFuelCharge', false)
        this.$set(order, 'applyFuelCharge', true)
      }
    },
    getDoneBillingUpdateFields (order) {
      const updateFields = {
        price: odooBilling.normalizePrice(order.price),
        fuelCharge: this.doneOrderFuelValue(order)
      }

      if (order.includedFuelCharge !== undefined) updateFields.includedFuelCharge = order.includedFuelCharge
      if (order.applyFuelCharge !== undefined) updateFields.applyFuelCharge = order.applyFuelCharge

      return updateFields
    },
    saveDoneBillingFields (invoice) {
      return Promise.all(invoice.orders.map(order =>
        this.orderRef(order['.key']).update(this.getDoneBillingUpdateFields(order))))
    },
    setEntryPrice (order, entry, value) {
      const price = Number(value) || 0
      const orderKey = order['.key']
      db.ref(`database/transporter/${this.companyUID}/history/${orderKey}/${entry.key}`)
        .update({ price, prix: price })
      this.orderRef(orderKey).child('historySync').child(entry.key).update({ price, prix: price })
      publishOrderSyncRequest(orderKey, this.companyUID)
      this.$store.dispatch('newLog', { 'UID': this.uid, 'orderID': orderKey, 'numRef': entry.numRef || '', 'action': 'MODIFY_ORDER_HISTORY', 'date': Date.now() })
    },

    loadLocalState () {
      this.storageLoaded = true
      try {
        const saved = JSON.parse(localStorage.getItem(this.storageKey))
        this.groups = (saved && Array.isArray(saved.groups)) ? saved.groups : []
        this.ungroupedKeys = (saved && Array.isArray(saved.ungrouped)) ? saved.ungrouped : []
      } catch (e) {
        this.groups = []
        this.ungroupedKeys = []
      }
    },
    saveLocalState () {
      localStorage.setItem(this.storageKey, JSON.stringify({ groups: this.groups, ungrouped: this.ungroupedKeys }))
    },
    autoGroup () {
      if (!this.storageLoaded) this.loadLocalState()
      if (!this.orders.length) return

      this.groups = this.groups
        .map(group => {
          const live = group.orderKeys.filter(key => this.ordersByKey[key])
          if (!live.length) return { ...group, orderKeys: [] }
          const payerId = this.ordersByKey[live[0]].payer || 'no-payer'
          return { ...group, orderKeys: live.filter(key => (this.ordersByKey[key].payer || 'no-payer') === payerId) }
        })
        .filter(group => group.orderKeys.length > 0)
      this.ungroupedKeys = this.ungroupedKeys.filter(key => this.ordersByKey[key])

      const assigned = new Set(this.ungroupedKeys)
      this.groups.forEach(group => group.orderKeys.forEach(key => assigned.add(key)))

      const byPayer = {}
      this.orders.forEach(order => {
        if (assigned.has(order['.key']) || this.invoiceStatus([order]) !== 'draft') return
        const payerId = order.payer || 'no-payer'
        ;(byPayer[payerId] = byPayer[payerId] || []).push(order)
      })

      Object.keys(byPayer).forEach(payerId => {
        let rest = byPayer[payerId]

        const byRef = {}
        rest.forEach(order => {
          if (order.numRef) (byRef[order.numRef] = byRef[order.numRef] || []).push(order)
        })
        Object.keys(byRef).forEach(ref => {
          if (byRef[ref].length >= 2) {
            this.groups.push({
              id: odooBilling.buildOdooInvoiceGroupId(this.companyUID),
              orderKeys: byRef[ref].map(order => order['.key'])
            })
            rest = rest.filter(order => !byRef[ref].includes(order))
          }
        })

        const byTax = {}
        rest.forEach(order => {
          const code = odooBilling.getDefaultTaxCode(order)
          ;(byTax[code] = byTax[code] || []).push(order)
        })
        Object.keys(byTax).forEach(code => {
          if (byTax[code].length >= 2) {
            this.groups.push({
              id: odooBilling.buildOdooInvoiceGroupId(this.companyUID),
              orderKeys: byTax[code].map(order => order['.key'])
            })
          }
        })
      })

      this.saveLocalState()
    },
    dissolveGroup (group) {
      this.ungroupedKeys = [...this.ungroupedKeys, ...group.orders.map(order => order['.key'])]
      this.groups = this.groups.filter(stored => stored.id !== group.id)
      this.saveLocalState()
    },

    canDrag (invoice) {
      return this.canEdit && invoice.status === 'draft'
    },
    onDragStart (event, order) {
      const payerId = order.payer || 'no-payer'
      let keys = [order['.key']]
      if (this.selectedOrders[order['.key']]) {
        const selected = this.validSelectedKeys.filter(key =>
          (this.ordersByKey[key].payer || 'no-payer') === payerId)
        if (selected.length > 1) keys = selected
      }
      this.drag = { keys, payerId }
      event.dataTransfer.effectAllowed = 'move'
      try { event.dataTransfer.setData('text/plain', keys.join(',')) } catch (e) {}
      this.setDragGhost(event, keys.length > 1
        ? `${keys.length} ${this.$t('ToBillBoard.orders')}`
        : `${order['.key']} · ${this.truncate(order.model, 18)}`)
    },
    setDragGhost (event, label) {
      if (!event.dataTransfer || typeof event.dataTransfer.setDragImage !== 'function') return
      const ghost = document.createElement('div')
      ghost.textContent = label
      ghost.style.cssText = 'position:absolute;top:-1000px;left:-1000px;z-index:2000;' +
        'padding:5px 14px;background:#fff;border:1px solid #f7941e;border-radius:4px;' +
        'font-size:12px;font-weight:600;color:#2c4f7c;white-space:nowrap;' +
        'box-shadow:0 2px 8px rgba(0,0,0,0.2);'
      document.body.appendChild(ghost)
      this.dragGhostEl = ghost
      try { event.dataTransfer.setDragImage(ghost, 14, 14) } catch (e) {}
      setTimeout(() => this.removeDragGhost(), 0)
    },
    removeDragGhost () {
      if (this.dragGhostEl && this.dragGhostEl.parentNode) {
        this.dragGhostEl.parentNode.removeChild(this.dragGhostEl)
      }
      this.dragGhostEl = null
    },
    onDragEnd () {
      this.removeDragGhost()
      this.drag = null
      this.dropTarget = null
    },
    onDragOver (event, target) {
      if (!this.drag) return
      event.dataTransfer.dropEffect = 'move'
      const key = `${target.type}:${target.id}`
      if (this.dropTarget && this.dropTarget.key === key) return
      let ok = this.drag.payerId === target.payerId
      let warning = ok ? '' : this.$t('ToBillBoard.different_account')
      if (ok && target.type === 'group' && target.invoice.status !== 'draft') {
        ok = false
        warning = this.$t('ToBillBoard.invoice_sent_locked')
      }
      this.dropWarning = warning
      this.dropTarget = { key, ok }
    },
    onDragLeave (event, key) {
      if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) return
      if (this.dropTarget && this.dropTarget.key === key) this.dropTarget = null
    },
    async onDrop (target) {
      const drag = this.drag
      this.drag = null
      this.dropTarget = null
      if (!drag) return
      if (drag.payerId !== target.payerId) {
        this.notify(this.$t('ToBillBoard.different_account'), 'error')
        return
      }
      if (target.type === 'group' && target.invoice.status !== 'draft') {
        this.notify(this.$t('ToBillBoard.invoice_sent_locked'), 'error')
        return
      }
      await this.moveOrders(drag.keys, target)
    },
    moveOrders (keys, target) {
      this.ungroupedKeys = this.ungroupedKeys.filter(key => !keys.includes(key))
      this.groups.forEach(group => {
        if (target.type === 'group' && group.id === target.id) return
        group.orderKeys = group.orderKeys.filter(key => !keys.includes(key))
      })

      if (target.type === 'group') {
        const group = this.groups.find(stored => stored.id === target.id)
        if (group) group.orderKeys = [...group.orderKeys.filter(key => !keys.includes(key)), ...keys]
      } else if (target.type === 'new') {
        this.groups.push({ id: odooBilling.buildOdooInvoiceGroupId(this.companyUID), orderKeys: [...keys] })
      } else {
        this.ungroupedKeys = [...this.ungroupedKeys, ...keys]
      }

      this.groups = this.groups.filter(group => group.orderKeys.length > 0)
      keys.forEach(key => {
        if (this.selectedOrders[key]) this.$delete(this.selectedOrders, key)
      })
      this.saveLocalState()
    },
    toggleOrderSelected (orderKey) {
      this.$set(this.selectedOrders, orderKey, !this.selectedOrders[orderKey])
    },
    groupSelected () {
      const keys = this.validSelectedKeys
      if (keys.length < 2) return
      const payerIds = [...new Set(keys.map(key => this.ordersByKey[key].payer || 'no-payer'))]
      if (payerIds.length > 1) {
        this.notify(this.$t('ToBillBoard.group_needs_same_payer'), 'error')
        return
      }
      const payerId = payerIds[0]
      this.moveOrders(keys, { type: 'new', id: payerId, payerId })
      this.$set(this.openSections, payerId, true)
      this.notify(this.$t('ToBillBoard.group_created'), 'success')
    },
    removeFromGroup (group, order) {
      const stored = this.groups.find(item => item.id === group.id)
      if (!stored) return
      const orderKey = order['.key']
      const remaining = stored.orderKeys.filter(key => key !== orderKey)
      if (remaining.length <= 1) {
        this.groups = this.groups.filter(item => item.id !== group.id)
        this.ungroupedKeys = [...new Set([...this.ungroupedKeys, orderKey, ...remaining])]
      } else {
        stored.orderKeys = remaining
        this.ungroupedKeys = [...new Set([...this.ungroupedKeys, orderKey])]
      }
      this.saveLocalState()
    },

    setNoteName (user) {
      const safeUser = user || {}
      if (!safeUser.firstName || !safeUser.lastName) return this.$store.state.userInfo.name
      return `${safeUser.firstName} ${safeUser.lastName}`
    },
    async fetchFreshInvoice (invoice, allowQueued = false) {
      const freshOrders = []
      for (const order of invoice.orders) {
        const snapshot = await this.orderRef(order['.key']).once('value')
        if (snapshot.exists()) freshOrders.push({ '.key': order['.key'], ...snapshot.val() })
      }
      const freshStatus = this.invoiceStatus(freshOrders)
      if (!freshOrders.length || (freshStatus !== 'draft' && !(allowQueued && this.isResendableQueuedOrders(freshOrders)))) return null
      const fresh = this.invoiceObject(freshOrders, invoice.odooInvoiceGroupId || '')
      if (!fresh.payerOdooPartnerId && fresh.payer) {
        const dealerSnapshot = await db.ref('users/dealers/' + fresh.payer).once('value')
        const dealer = dealerSnapshot.val() || {}
        fresh.payerOdooPartnerId = dealer.odooPartnerId || ''
        fresh.payerOdooPartnerName = dealer.odooPartnerName || ''
      }
      const billingDate = this.normalizeBillingDate(invoice.billingDate || fresh.billingDate)
      fresh.billingDate = billingDate
      fresh.orders.forEach(order => { order.billingDate = billingDate })
      return fresh
    },
    async sendInvoices (invoices, options = {}) {
      const candidates = invoices.filter(invoice =>
        invoice.status === 'draft' || (options.includeResend && this.isResendableQueuedInvoice(invoice)))
      if (!candidates.length || this.sending) return
      this.sending = true
      try {
        const sendItems = []
        let conflicted = 0
        for (const invoice of candidates) {
          const isResend = options.includeResend && invoice.status === 'queued'
          const fresh = await this.fetchFreshInvoice(invoice, isResend)
          if (!fresh) continue
          if (fresh.orders.some(order => this.computePriceConflict(order))) {
            conflicted++
            continue
          }
          sendItems.push({ invoice: fresh, isResend })
        }
        if (conflicted) {
          this.notify(`${conflicted} ${this.$t('ToBillBoard.price_conflict_skipped')}`, 'error')
        }
        if (!sendItems.length) return

        const batchId = `${this.companyUID}-${Date.now()}`
        const payloads = sendItems.map(item =>
          odooBilling.buildOdooInvoicePayload(item.invoice, batchId, this.defaultFuelCharge))

        for (const item of sendItems) {
          const invoice = item.invoice
          const grouped = invoice.orders.length > 1
          for (const [index, order] of invoice.orders.entries()) {
            const orderKey = order['.key']
            const updateFields = {
              price: Number(order.price) || 0,
              taxes: invoice.taxes,
              fuelCharge: order.fuelCharge !== undefined ? order.fuelCharge : '',
              taxesComment: invoice.taxesComment || order.taxesComment || '',
              billingDate: invoice.billingDate,
              odooSyncStatus: 'sync_requested',
              odooSyncRequestedAt: Date.now(),
              odooInvoiceBatchId: batchId,
              odooInvoiceGroupEnabled: grouped,
              odooInvoiceGroupId: grouped ? invoice.odooInvoiceGroupId : '',
              odooInvoiceGroupIndex: grouped ? index + 1 : '',
              odooInvoiceGroupSize: grouped ? invoice.orders.length : ''
            }
            if (order.includedFuelCharge !== undefined) updateFields.includedFuelCharge = order.includedFuelCharge
            if (order.applyFuelCharge !== undefined) updateFields.applyFuelCharge = order.applyFuelCharge

            await this.orderRef(orderKey).update(updateFields)
            await this.orderRef(orderKey).child('notes').push({
              date: Date.now(),
              name: this.setNoteName(this.userInfo),
              text: `${item.isResend ? 'RESENT' : 'SENT'} TO ODOO: ${odooBilling.normalizePrice(odooBilling.getOrderSubtotal(order, this.defaultFuelCharge)).toFixed(2)}$`
            })
            this.$store.dispatch('newLog', { 'UID': this.uid, 'action': 'FAST_BILLING', 'orderID': orderKey, 'date': Date.now() })
          }
        }

        await publishBillBatchSyncRequest(payloads, this.companyUID, batchId)
        this.notify(`${sendItems.length} ${this.$t('ToBillBoard.sent_to_queue')}`, 'success')
      } catch (error) {
        console.error('Error while sending Odoo invoice(s):', error)
        this.notify(this.$t('ToBillBoard.send_error'), 'error')
      } finally {
        this.sending = false
      }
    },
    sendInvoice (invoice) {
      this.sendInvoices([invoice])
    },
    resendInvoice (invoice) {
      this.sendInvoices([invoice], { includeResend: true })
    },
    sendCustomer (section) {
      this.sendInvoices(section.invoices)
    },
    toggleCustomerSelected (payerId) {
      this.$set(this.selectedCustomers, payerId, !this.selectedCustomers[payerId])
    },
    async sendSelected () {
      await this.sendInvoices(this.selectedInvoices)
      this.selectedCustomers = {}
    },

    async finishVerifiedInvoice (invoice) {
      if (!this.invoiceVerified(invoice)) return
      await this.saveDoneBillingFields(invoice)

      for (const order of invoice.orders) {
        const orderKey = order['.key']
        await this.orderRef(orderKey).update({
          isBilling: false,
          status: 'Terminée',
          odooBillingCompletedAt: Date.now()
        })
        this.$store.dispatch('newLog', { 'UID': this.uid, 'action': 'FAST_BILLING', 'orderID': orderKey, 'date': Date.now() })
      }
    },
    async markInvoiceDone (invoice) {
      if (this.sending) return
      this.sending = true
      try {
        await this.finishVerifiedInvoice(invoice)
        this.notify(this.$t('ToBillBoard.mark_done_success'), 'success')
      } catch (error) {
        console.error('Error while marking Odoo invoice done:', error)
        this.notify(this.$t('ToBillBoard.mark_done_error'), 'error')
      } finally {
        this.sending = false
      }
    },
    async markAllSyncedDone () {
      if (this.sending) return
      this.sending = true
      const synced = this.allInvoices.filter(invoice => invoice.status === 'done')
      try {
        for (const invoice of synced) {
          await this.finishVerifiedInvoice(invoice)
        }
        if (synced.length) this.notify(this.$t('ToBillBoard.mark_done_success'), 'success')
      } catch (error) {
        console.error('Error while marking Odoo invoices done:', error)
        this.notify(this.$t('ToBillBoard.mark_done_error'), 'error')
      } finally {
        this.sending = false
      }
    },

    sectionTotal (section) {
      return section.invoices.reduce((total, invoice) => total + invoice.total, 0)
    },
    draftsOf (section) {
      return section.invoices.filter(invoice => invoice.status === 'draft').length
    },
    queuedOf (section) {
      return section.invoices.filter(invoice => invoice.status === 'queued').length
    },
    doneOf (section) {
      return section.invoices.filter(invoice => invoice.status === 'done').length
    },
    dropClass (key) {
      if (!this.dropTarget || this.dropTarget.key !== key) return ''
      return this.dropTarget.ok ? 'drop-ok' : 'drop-bad'
    }
  },

  beforeDestroy () {
    clearTimeout(this.feedbackTimer)
    this.removeDragGhost()
  }
}
</script>

<style scoped>
.board-card {
  position: relative;
}
.board-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid #eceeef;
}
.board-titles {
  margin-right: auto;
}
.board-subtitle {
  font-size: 12px;
  color: #868e96;
}
.board-filter {
  width: 170px;
  height: 34px;
}
.selection-chip {
  background: #f7941e;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 999px;
}
.board-body {
  background: #f6f7f9;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-height: 200px;
}
.customer-section {
  background: #fff;
  border: 1px solid #e3e6ea;
  border-radius: 8px;
  overflow: hidden;
}
.customer-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
}
.customer-header:hover {
  background: #fafbfc;
}
.customer-header .chevron {
  color: #adb5bd;
  width: 12px;
}
.customer-check {
  display: inline-flex;
  align-items: center;
  margin: 0;
  padding: 4px;
  cursor: pointer;
}
.customer-check input {
  width: 17px;
  height: 17px;
  cursor: pointer;
  accent-color: #f7941e;
}
.customer-name {
  font-weight: 700;
  color: #2c4f7c;
}
.customer-status, .customer-counts {
  font-size: 12px;
  color: #868e96;
}
.customer-total {
  font-weight: 700;
  font-size: 13px;
}
.customer-body {
  border-top: 1px solid #f1f3f5;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.flex-spacer {
  flex: 1;
}
.group-card {
  border: 1px solid #e3e6ea;
  border-radius: 6px;
  background: #fff;
  transition: box-shadow 120ms, border-color 120ms;
}
.group-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 10px;
  background: #fafbfc;
  border-bottom: 1px solid #f1f3f5;
  border-radius: 6px 6px 0 0;
}
.group-header .fa-file-text-o {
  color: #2c4f7c;
}
.group-name {
  font-weight: 600;
  font-size: 13px;
}
.group-counts {
  font-size: 11px;
  color: #adb5bd;
}
.reason-chip {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 1px 7px;
  border-radius: 999px;
  border: 1px solid;
}
.reason-reference {
  background: #fff4e8;
  color: #c05f00;
  border-color: #f3ce9f;
}
.reason-tax {
  background: #e9f2fd;
  color: #1c5dab;
  border-color: #b3d2f3;
}
.reason-manual {
  background: #f1f3f5;
  color: #6c757d;
  border-color: #dee2e6;
}
.group-total {
  font-weight: 700;
  font-size: 13px;
}
.group-breakdown {
  display: flex;
  justify-content: flex-end;
  gap: 14px;
  padding: 5px 10px;
  font-size: 11px;
  color: #868e96;
  background: #fafbfc;
  border-top: 1px solid #f1f3f5;
  border-radius: 0 0 6px 6px;
}
.group-breakdown .grand {
  font-size: 12px;
  color: #212529;
}
.order-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 5px 10px;
  border-bottom: 1px solid #f6f7f9;
  font-size: 12px;
}
.order-row[draggable="true"] {
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
}
.row-check {
  display: inline-flex;
  align-items: center;
  margin: 0;
  padding: 2px;
  cursor: pointer;
}
.row-check input {
  width: 15px;
  height: 15px;
  margin: 0;
  cursor: pointer;
  accent-color: #f7941e;
}
.remove-order:hover {
  color: #dc3545;
}
.order-row:hover {
  background: #fff8ef;
}
.order-row-wrap:last-child .order-row {
  border-bottom: none;
}
.grip {
  color: #ced4da;
  letter-spacing: 1px;
}
.grip-off {
  visibility: hidden;
}
.order-key {
  font-family: monospace;
  font-weight: 600;
  color: #2c4f7c;
  min-width: 60px;
}
.order-ref {
  color: #868e96;
  min-width: 100px;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.order-vin {
  font-family: monospace;
  color: #868e96;
}
.order-date {
  color: #868e96;
  white-space: nowrap;
}
.order-model {
  font-weight: 500;
  color: #495057;
}
.order-route {
  color: #868e96;
  flex: 1;
  min-width: 140px;
}
.route-arrow {
  color: #ced4da;
}
.route-loc {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.order-price {
  font-weight: 700;
  min-width: 70px;
  text-align: right;
}
.order-details {
  padding: 6px 10px 10px 32px;
  background: #fafbfc;
  border-bottom: 1px solid #f1f3f5;
  font-size: 12px;
  color: #6c757d;
}
.details-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 4px 0 6px;
}
.details-block {
  margin-top: 6px;
}
.details-title {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #adb5bd;
  margin-bottom: 3px;
}
.details-empty {
  margin: 0;
  font-size: 12px;
  color: #adb5bd;
}
.history-table {
  width: 100%;
  background: #fff;
  border: 1px solid #f1f3f5;
  border-radius: 4px;
  border-collapse: collapse;
  font-size: 11.5px;
}
.history-table th {
  text-align: left;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #adb5bd;
  padding: 4px 8px;
  border-bottom: 1px solid #f1f3f5;
}
.history-table td {
  padding: 4px 8px;
  border-bottom: 1px solid #f6f7f9;
  color: #495057;
}
.history-table .num {
  text-align: right;
}
.history-table tfoot td {
  background: #fafbfc;
  border-bottom: none;
  font-size: 11px;
  color: #6c757d;
}
.history-table tfoot .history-over td,
.history-table .history-over td {
  background: #fdecea;
  color: #c0392b;
}
.route-cell {
  white-space: nowrap;
}
.history-price-input {
  width: 90px;
  height: 26px;
  font-size: 12px;
  padding: 2px 6px;
  display: inline-block;
  text-align: right;
}
.conflict-msg {
  margin: 4px 0 0;
  font-size: 12px;
  font-weight: 600;
  color: #c0392b;
}
.notes-list {
  margin: 0;
  padding: 0;
  list-style: none;
  background: #fff;
  border: 1px solid #f1f3f5;
  border-radius: 4px;
}
.notes-list li {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 8px;
  border-bottom: 1px solid #f6f7f9;
}
.notes-list li:last-child {
  border-bottom: none;
}
.note-meta {
  font-size: 11px;
  color: #adb5bd;
  white-space: nowrap;
}
.note-text {
  color: #495057;
}
.order-row.price-conflict {
  background: #fdecea;
  box-shadow: inset 3px 0 0 #dc3545;
}
.order-row.price-conflict:hover {
  background: #fbdcd9;
}
.chip-neutral {
  background: #f1f3f5;
  color: #6c757d;
}
.mono {
  font-family: monospace;
  font-size: 11px;
}
.inline-control {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #adb5bd;
}
.done-billing-field {
  color: #495057;
}
.done-billing-field .form-control {
  border-color: #f3ce9f;
  background: #fff;
}
.inline-control .form-control,
.date-input {
  height: 28px;
  font-size: 12px;
  padding: 2px 6px;
  width: auto;
  display: inline-block;
}
.fuel-input {
  width: 64px;
}
.price-input {
  width: 90px;
}
.date-input {
  width: 130px;
}
.ungrouped-zone {
  border: 1px dashed #ced4da;
  border-radius: 6px;
  background: #fafbfc;
  transition: border-color 120ms, background 120ms;
}
.zone-label {
  padding: 5px 10px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #adb5bd;
  display: flex;
  gap: 10px;
  align-items: center;
}
.zone-empty {
  padding: 0 10px 8px;
  margin: 0;
  font-size: 12px;
  color: #adb5bd;
}
.solo-row-wrap {
  background: #fff;
}
.new-group-zone {
  border: 1px dashed #ced4da;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12px;
  color: #adb5bd;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: border-color 120ms, background 120ms;
}
.drop-ok {
  border-color: #f7941e !important;
  box-shadow: 0 0 0 2px rgba(247, 148, 30, 0.25);
  background: #fff8ef;
}
.drop-bad {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.2);
  background: #fdf3f4;
}
.drop-warning {
  font-size: 11px;
  font-weight: 600;
  color: #dc3545;
}
.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
}
.chip-queued {
  background: #e0ecfb;
  color: #1c5dab;
}
.chip-done {
  background: #def2de;
  color: #1e7b34;
}
.send-cluster {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.btn-icon {
  border: none;
  background: transparent;
  color: #adb5bd;
  padding: 2px 6px;
  cursor: pointer;
}
.btn-icon:hover {
  color: #495057;
}
.btn-sm {
  padding: 2px 10px;
  font-size: 12px;
}
.board-footer {
  position: sticky;
  bottom: 2.25rem;
  z-index: 20;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  padding: 8px 16px;
  background: #233b53;
  color: #fff;
  font-size: 13px;
}
.footer-muted {
  color: #b9c6d4;
  font-size: 12px;
}
.footer-muted b {
  color: #fff;
}
.footer-divider {
  width: 1px;
  height: 16px;
  background: rgba(255, 255, 255, 0.25);
}
.footer-total {
  font-weight: 700;
}
.board-toast {
  position: fixed;
  bottom: 70px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1100;
  padding: 8px 18px;
  border-radius: 6px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  background: #233b53;
}
.toast-success {
  background: #1e7b34;
}
.toast-error {
  background: #c0392b;
}
</style>
